import React, { useEffect, useState, useMemo } from 'react';
import { API as api } from '../services/api';
import { useAuth } from '../hooks/authContext';
import Navbar from '../components/Navbar';

const TABS = [
  { id: 'all', label: 'All users' },
  { id: 'pending', label: 'Pending approval' },
  { id: 'approved', label: 'Approved' },
];

export default function ManageUsersPage() {
  const { isAdmin, loading: authLoading, user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState(null);

  const loadUsers = async (tab) => {
    try {
      setError(null);
      setLoading(true);

      let url = '/users';
      if (tab === 'pending') url = '/users/pending';
      if (tab === 'approved') url = '/users/approved';

      const res = await api.get(url);
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Load users error:', err);
      setError(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAdmin) {
      loadUsers(activeTab);
    }
  }, [authLoading, isAdmin, activeTab]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this user?')) return;
    setActionLoadingId(id);
    try {
      await api.patch(`/users/${id}/approve`);
      // refresh current tab data
      await loadUsers(activeTab);
    } catch (err) {
      console.error('Approve user error:', err);
      setError(err.response?.data?.message || 'Failed to approve user.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (currentUser && currentUser.id === id) {
      alert("You can't delete your own account from here.");
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    setActionLoadingId(id);
    try {
      await api.delete(`/users/${id}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      console.error('Delete user error:', err);
      setError(err.response?.data?.message || 'Failed to delete user.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const counts = useMemo(() => {
    console.log('Users')
    console.log(users)
    const total = users.length;
    const approved = users?.filter((u) => u.is_approved).length;
    const pending = users?.filter((u) => !u.is_approved).length;
    return { total, approved, pending };
  }, [users]);

  if (authLoading) {
    return (
      <div className="users-page">
        <div className="users-container">
          <div className="users-loading">Checking permissions…</div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="users-page">
        <div className="users-container">
          <div className="users-forbidden">
            <h2 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>
              You do not have access to this page.
            </h2>
            <p style={{ fontSize: '0.875rem' }}>
              Only administrators can manage user accounts.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
  <div>
  <Navbar />
  <div className="users-page">
      <div className="users-container">
        <div className="users-header">
          <div className="users-title-block">
            <h1>Manage Users</h1>
            <p>View all users, approve pending accounts, and remove access.</p>
          </div>
          <div className="users-badge">
            Admin Console
          </div>
        </div>

        <div className="users-card">
          <div className="users-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`users-tab-btn ${
                  activeTab === tab.id ? 'active' : ''
                }`}
                onClick={() => setActiveTab(tab.id)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>

          {error && <div className="users-error">{error}</div>}

          <div className="users-summary">
            <div>
              <span style={{ fontWeight: 500 }}>Current view:</span>{' '}
              <span style={{ textTransform: 'capitalize' }}>{activeTab}</span>
            </div>
            <div className="users-summary-right">
              <span>Total: {counts.total}</span>
              <span>Approved: {counts.approved}</span>
              <span>Pending: {counts.pending}</span>
            </div>
          </div>

          <div className="users-table-wrapper">
            {loading ? (
              <div className="users-loading">Loading users…</div>
            ) : users.length === 0 ? (
              <div className="users-empty">
                No users found for this view.
              </div>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th style={{ width: '1%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const created =
                      u.created_at || u.createdAt
                        ? new Date(u.created_at || u.createdAt).toLocaleString()
                        : '—';

                    return (
                      <tr key={u.id}>
                        <td className="users-col-email">{u.email}</td>
                        <td className="users-col-name">
                          {u.display_name || '—'}
                        </td>
                        <td className="users-col-role">
                          {u.role || 'user'}
                        </td>
                        <td>
                          {u.is_approved ? (
                            <span className="badge badge-approved">
                              Approved
                            </span>
                          ) : (
                            <span className="badge badge-pending">
                              Pending
                            </span>
                          )}
                        </td>
                        <td>{created}</td>
                        <td>
                          <div className="users-actions">
                            {!u.is_approved && (
                              <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => handleApprove(u.id)}
                                disabled={actionLoadingId === u.id}
                              >
                                {actionLoadingId === u.id
                                  ? 'Approving…'
                                  : 'Approve'}
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn-ghost btn-ghost-danger"
                              onClick={() => handleDelete(u.id)}
                              disabled={actionLoadingId === u.id}
                            >
                              {actionLoadingId === u.id
                                ? 'Removing…'
                                : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>

  );
}